import { type ComponentType, useState, useCallback, useEffect, useRef } from 'react';

import type { ChatProps } from '@/components/chat/ChatTypes';
import { trackInquiryGroupTabSwitched } from '@/util/analyticsEvents';

export type InquiryGroupConversationRoute = {
  key: string;
  conversationId: string;
  title: string;
};

/**
 * Grouped conversations with local state for responsive tab switching.
 * On layouts that sync route state, the active grouped conversation is mirrored into the route.
 * On native and compact web layouts, the active conversation stays local so only the message content
 * changes instead of the entire fullscreen chat screen responding to route updates.
 */
export function InquiryGroupConversations({
  routes,
  selectedConversationId,
  userId,
  conversation,
  recipient,
  conversationStatus,
  inquiry,
  onSelectConversation,
  shouldSyncSelectionToRoute,
  ChatContainerComponent,
  tabGrouping = 'send',
}: {
  routes: InquiryGroupConversationRoute[];
  selectedConversationId: string;
  userId: string;
  conversation: ChatProps['conversation'];
  recipient: ChatProps['recipient'];
  conversationStatus: ChatProps['conversationStatus'];
  inquiry: ChatProps['inquiry'];
  onSelectConversation: (conversationId: string) => void;
  shouldSyncSelectionToRoute: boolean;
  ChatContainerComponent: ComponentType<ChatProps>;
  /** Where the tabs came from, reported with each switch: one send, or an inquiry group card. */
  tabGrouping?: 'question' | 'send';
}) {
  const [localSelectedId, setLocalSelectedId] = useState(selectedConversationId);

  const prevSelectedIdRef = useRef(selectedConversationId);

  useEffect(() => {
    if (prevSelectedIdRef.current !== selectedConversationId) {
      prevSelectedIdRef.current = selectedConversationId;

      setLocalSelectedId(selectedConversationId);
    }
  }, [selectedConversationId]);

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      if (!routes.some((item) => item.conversationId === conversationId)) {
        return;
      }

      if (conversationId !== localSelectedId) {
        Promise.resolve(
          trackInquiryGroupTabSwitched({
            fromConversationId: localSelectedId,
            toConversationId: conversationId,
            inquiryGroupSize: routes.length,
            inquiryGroupsEnabled: true,
            tabGrouping,
            source: 'mobile_chat_tabs',
          })
        ).catch(() => undefined);
      }

      setLocalSelectedId(conversationId);

      if (shouldSyncSelectionToRoute) {
        onSelectConversation(conversationId);
      }
    },
    [localSelectedId, onSelectConversation, routes, shouldSyncSelectionToRoute, tabGrouping]
  );

  return (
    <ChatContainerComponent
      conversationStatus={conversationStatus}
      userId={userId}
      conversation={conversation}
      recipient={recipient}
      inquiry={inquiry}
      activeMessageConversationId={localSelectedId}
      groupedTabs={{
        routes: routes.map((item) => ({
          conversationId: item.conversationId,
          title: item.title ?? '',
        })),
        activeConversationId: localSelectedId,
        onSelectConversation: handleSelectConversation,
      }}
    />
  );
}
